/**
 * STOCK (FASE 9.2) — saldos, operaciones e histórico
 *
 *   /inventario/stock                         saldos (artículo × almacén) con estado
 *   /inventario/movimientos?tab=operaciones   operaciones (borradores, confirmadas, anuladas)
 *   /inventario/movimientos?tab=ledger        «Histórico»: movimientos confirmados (sólo lectura)
 *
 * FASE 9.2-C: en producto el ledger se llama «Histórico». El contrato técnico
 * (`?tab=ledger`, `/api/inventario/movimientos`, `movimientos_stock`, el
 * componente `Ledger`) NO cambia: sólo las etiquetas visibles.
 *   /inventario/operaciones/nueva?tipo=…      formulario propio de cada tipo, multilínea
 *   /inventario/operaciones/:id               ficha: borrador editable · confirmar · anular · rectificar
 *
 * EL STOCK NO SE EDITA: aquí no hay ningún campo «saldo». Un borrador no
 * mueve nada; al confirmar, la base escribe el ledger y recalcula. Los
 * saldos que se ven mientras se edita son INFORMATIVOS (la regla se aplica
 * al confirmar). Artículo y almacén se eligen con el selector buscable.
 *
 * FASE 9.4: si el artículo controla lotes, la línea lleva su lote (con alta
 * en línea); si controla series, la cantidad SON sus series (una por línea
 * del área de texto: se pegan o se escanean). El Histórico muestra lote y
 * serie, y «Ver detalle» desglosa el saldo por lote / series.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  api,
  FalloApi,
  type EstadoOperacionStock,
  type EstadoStock,
  type EventoOperacionStock,
  type LineaOperacionStock,
  type MovimientoStock,
  type OpcionAlmacenStock,
  type OpcionArticuloStock,
  type OpcionLote,
  type OpcionSerie,
  type OperacionStockDetalle,
  type OperacionStockResumen,
  type SaldoStock,
  type SentidoStock,
  type TipoOperacionStock,
} from '../api.ts';
import { BarraAcciones, EmptyState, Error as ErrorCaja, Estado, fecha, fechaHora, FiltrosResponsive, Modal, hoyLocal, Panel, SinResultados, useDatos, Vacio } from '../componentes/base.tsx';
import { CambiosEvento } from '../componentes/cambios-historial.tsx';
import { ESTADOS_OPERACION_STOCK, estadoOperacionStock } from '../estados.ts';
import { Buscador } from '../componentes/buscador.tsx';
import { Escaner } from '../componentes/escaner.tsx';
import { anadirSerie, opcionArticulo, opcionLote, sumar } from '../escaner/opciones.ts';
import type { Lectura, UsoEscaneo } from '../escaner/tipos.ts';
import { PieHistorial, useHistorial } from '../componentes/historial.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
//  Ciclo de módulos inocuo: trazabilidad.tsx usa piezas de aquí y viceversa, sólo al renderizar.
import { DetalleStockModal, EditorSeries, ListaLotes, ListaSeries, NuevaSerie, NuevoLote, SelectorLote, seriesUnicas as seriesDe } from './trazabilidad.tsx';
import { codigoAlmacen, codigoVisible } from '../componentes/codigo-articulo.tsx';
import { StockAFecha } from './historico.tsx';

const buscarSeriesDisponibles = (idArticulo: string, idAlmacen: string, idLote: string | null) => async (texto: string) => {
  const p = new URLSearchParams({ idArticulo, idAlmacen, enStock: 'true', texto, limite: '20' });
  if (idLote !== null) p.set('idLote', idLote);
  return (await api.get<{ filas: OpcionSerie[] }>(`/api/inventario/opciones/series?${p.toString()}`)).filas;
};
/** «Por lotes» / «Con series»: cómo se controla el stock de un artículo (bajo su nombre). */
export function ControlTraza({ a }: { a: { controlaLotes?: boolean | undefined; controlaSeries?: boolean | undefined; controlaCaducidad?: boolean | undefined } }): ReactNode {
  const t = [a.controlaLotes === true && (a.controlaCaducidad === true ? 'Por lotes con caducidad' : 'Por lotes'), a.controlaSeries === true && 'Con números de serie'].filter(Boolean);
  return t.length === 0 ? null : <div className="pequena suave control-traza">{t.join(' · ')}</div>;
}
/** Lote (con caducado) y series de una línea, bajo el nombre del artículo. */
export function TrazaLinea({ lote, series }: { lote?: LineaOperacionStock['lote']; series?: LineaOperacionStock['series'] }): ReactNode {
  if ((lote ?? null) === null && (series ?? []).length === 0) return null;
  return (
    <div className="pequena traza-linea">
      {lote != null && <>Lote <Link className="mono" to={`/inventario/lotes/${lote.id}`}>{lote.codigo}</Link>{lote.caducado && <span className="etiqueta error">Caducado</span>}</>}
      {(series ?? []).length > 0 && (
        <details className="series-linea"><summary>{series!.length} serie(s)</summary>
          <span className="mono">{series!.map((x, i) => <span key={x.id}>{i > 0 && ', '}<Link to={`/inventario/series/${x.id}`}>{x.numero}</Link></span>)}</span>
        </details>
      )}
    </div>
  );
}

export const TIPOS_OPERACION: Readonly<Record<TipoOperacionStock, string>> = {
  ENTRADA: 'Entrada', SALIDA: 'Salida', TRASLADO: 'Traslado', AJUSTE: 'Ajuste', DEVOLUCION: 'Devolución', CONSUMO_PT: 'Consumo de PT',
  CONSUMO_OT: 'Consumo de OT',
};
/** FASE 9.5 / 9.6-A: los que se crean a mano (CONSUMO_PT y CONSUMO_OT sólo los genera la validación de consumos). */
export const TIPOS_CREABLES: readonly TipoOperacionStock[] = ['ENTRADA', 'SALIDA', 'TRASLADO', 'AJUSTE', 'DEVOLUCION'];
const AYUDA_TIPO: Readonly<Record<TipoOperacionStock, string>> = {
  ENTRADA: 'Suma stock en un almacén (recepción, alta inicial…).',
  SALIDA: 'Resta stock de un almacén (consumo, entrega…).',
  TRASLADO: 'Mueve stock de un almacén a otro en una sola operación.',
  AJUSTE: 'Corrige el stock tras un recuento. Exige motivo y un permiso específico.',
  DEVOLUCION: 'Devuelve material a un almacén: suma stock.',
  CONSUMO_PT: 'Material usado en un parte de trabajo: se genera al validar sus consumos.',
  CONSUMO_OT: 'Material usado en una OT de Limpieza: se genera al validar sus consumos.',
};
//  UX-1C: [etiqueta, tono] del mapa central (`estados.ts`).
const ESTADOS_OPERACION = Object.fromEntries(
  Object.entries(ESTADOS_OPERACION_STOCK).map(([k, x]) => [k, [x.label, x.tone]]),
) as Readonly<Record<EstadoOperacionStock, [string, string]>>;
const ESTADOS_STOCK: Readonly<Record<EstadoStock, [string, string]>> = {
  SIN_STOCK: ['Sin stock', 'error'], BAJO: ['Bajo', 'aviso'], NORMAL: ['Normal', 'exito-suave'], SOBRESTOCK: ['Sobrestock', 'info'],
};
export const TEXTO_CONFIRMAR = 'Una vez confirmada, la operación no podrá editarse. Cualquier corrección deberá realizarse mediante una nueva operación.';
export const TEXTO_BORRADOR = 'El stock todavía no ha cambiado.';

const EstadoOperacion = ({ e }: { e: EstadoOperacionStock }): ReactNode => <Estado d={estadoOperacionStock(e)} />;
const EstadoSaldo = ({ e }: { e: EstadoStock }): ReactNode => <span className={`etiqueta ${ESTADOS_STOCK[e][1]}`}>{ESTADOS_STOCK[e][0]}</span>;
export const nombreAlmacen = (a: { codigo: string; codigoNegocio?: string | undefined; descripcion: string; centro: string | null }): string =>
  `${codigoAlmacen(a)} · ${a.descripcion}${a.centro === null ? '' : ` (${a.centro})`}`;
/** F13.5-A: código de negocio (no el token) + descripción. */
export const nombreArticulo = (a: { codigo: string; codigoNegocio?: string | undefined; descripcion: string }): string =>
  `${codigoVisible(a)} · ${a.descripcion}`;
const Cantidad = ({ v, u }: { v: string; u: string }): ReactNode => <span className={`cantidad-stock${v.startsWith('-') ? ' saldo-negativo' : ''}`}>{v} {u}</span>;

export function erroresDe(e: unknown): Record<string, string> {
  return e instanceof FalloApi ? (e.erroresDeCampo ?? {}) : {};
}
/** Error con el detalle de STOCK_INSUFICIENTE (artículo, almacén, disponible, solicitado) si la API lo da. */
export function ErrorStock({ error }: { error: unknown }): ReactNode {
  if (error === null) return null;
  const d = error instanceof FalloApi && error.codigo === 'STOCK_INSUFICIENTE' ? (error.detalles as Record<string, unknown> | undefined) : undefined;
  return (
    <>
      <ErrorCaja error={error} />
      {d !== undefined && (
        <p className="nota aviso" role="status">
          {String(d['articulo'])} en {String(d['almacen'])}: disponible <strong>{String(d['disponible'])}</strong>, solicitado <strong>{String(d['solicitado'])}</strong>.
        </p>
      )}
    </>
  );
}
export function Pestanas({ opciones, actual, fijar }: { opciones: readonly (readonly [string, string])[]; actual: string; fijar: (k: string) => void }): ReactNode {
  return (
    <nav className="tabs" role="tablist" aria-label="Secciones">
      {opciones.map(([k, t]) => (
        <a key={k} href={`?tab=${k}`} role="tab" aria-selected={actual === k} className={actual === k ? 'activo' : ''}
          onClick={(ev) => { ev.preventDefault(); fijar(k); }}>{t}</a>
      ))}
    </nav>
  );
}
export function Paginacion({ pagina, hayMas, fijar, total, porPagina }: { pagina: number; hayMas: boolean; fijar: (p: number) => void; total?: number; porPagina?: number }): ReactNode {
  if (pagina === 1 && !hayMas) return null;
  return (
    <nav className="fila" aria-label="Paginación" style={{ justifyContent: 'space-between', marginTop: 12 }}>
      <button type="button" className="pequeno" disabled={pagina <= 1} onClick={() => fijar(pagina - 1)}>Anterior</button>
      <span className="suave pequena">Página {pagina}{total !== undefined && porPagina !== undefined ? ` de ${Math.max(1, Math.ceil(total / porPagina))}` : ''}</span>
      <button type="button" className="pequeno" disabled={!hayMas} onClick={() => fijar(pagina + 1)}>Siguiente</button>
    </nav>
  );
}

/** Búsquedas en servidor para el selector (acotadas y por ámbito). */
export const buscarAlmacenes = async (texto: string): Promise<OpcionAlmacenStock[]> =>
  (await api.get<{ filas: OpcionAlmacenStock[] }>(`/api/inventario/opciones/almacenes?${new URLSearchParams({ texto, limite: '20' }).toString()}`)).filas;
export const buscarArticulos = (idAlmacen: string | null) => async (texto: string): Promise<OpcionArticuloStock[]> => {
  const p = new URLSearchParams({ texto, limite: '20' });
  if (idAlmacen !== null) p.set('idAlmacen', idAlmacen);
  return (await api.get<{ filas: OpcionArticuloStock[] }>(`/api/inventario/opciones/articulos?${p.toString()}`)).filas;
};

// ================================================================== saldos

const POR_PAGINA = 50;

/** Tabla de saldos; con `idArticulo` / `idAlmacen` fijo es la pestaña Stock de la ficha. */
export function TablaStock({ idArticulo, idAlmacen }: { idArticulo?: string; idAlmacen?: string }): ReactNode {
  const { puede } = useSesion();
  const [q, setQ] = useState({ texto: '', estado: '', incluirInactivos: false });
  const [almacen, setAlmacen] = useState<OpcionAlmacenStock | null>(null);
  const [pagina, setPagina] = useState(1);
  const [detalle, setDetalle] = useState<{ idArticulo: string; idAlmacen: string } | null>(null);
  const filtroAlmacen = idAlmacen ?? almacen?.id;
  const datos = useDatos(() => {
    const p = new URLSearchParams({ pagina: String(pagina), porPagina: String(POR_PAGINA) });
    if (q.texto !== '') p.set('texto', q.texto);
    if (q.estado !== '') p.set('estado', q.estado);
    if (q.incluirInactivos) p.set('incluirInactivos', 'true');
    if (idArticulo !== undefined) p.set('idArticulo', idArticulo);
    if (filtroAlmacen !== undefined) p.set('idAlmacen', filtroAlmacen);
    return api.get<{ filas: SaldoStock[]; total: number }>(`/api/inventario/stock?${p.toString()}`);
  }, [q.texto, q.estado, q.incluirInactivos, idArticulo, filtroAlmacen, pagina]);
  const fijar = <K extends keyof typeof q>(k: K, v: (typeof q)[K]): void => { setQ((a) => ({ ...a, [k]: v })); setPagina(1); };
  const contador = [q.estado !== '', q.incluirInactivos, almacen !== null].filter(Boolean).length;
  const ficha = idArticulo !== undefined || idAlmacen !== undefined;
  return (
    <section className="panel" aria-label="Stock">
      <div className="panel-cuerpo">
        <FiltrosResponsive contador={contador + (q.texto !== '' ? 1 : 0)}>
          <div className="filtros-rejilla">
            {idArticulo === undefined && (
              <div className="campo">
                <label htmlFor="stock-articulo">Artículo</label>
                <input id="stock-articulo" type="search" placeholder="Código o descripción" title="Buscar artículo por código o descripción"
                  value={q.texto} onChange={(ev) => fijar('texto', ev.target.value)} />
              </div>
            )}
            {idAlmacen === undefined && puede('operaciones_stock:crear') && (
              <Buscador<OpcionAlmacenStock> etiqueta="Almacén" valor={almacen} alElegir={(v) => { setAlmacen(v); setPagina(1); }}
                buscar={buscarAlmacenes} clave={(a) => a.id} texto={nombreAlmacen} placeholder="Todos los almacenes" />
            )}
            <label className="campo"><span>Estado</span>
              <select value={q.estado} onChange={(ev) => fijar('estado', ev.target.value)}>
                <option value="">Todos</option>
                {(Object.keys(ESTADOS_STOCK) as EstadoStock[]).map((e) => <option key={e} value={e}>{ESTADOS_STOCK[e][0]}</option>)}
              </select>
            </label>
            <label className="casilla"><input type="checkbox" checked={q.incluirInactivos} onChange={(ev) => fijar('incluirInactivos', ev.target.checked)} /> Incluir configuraciones inactivas</label>
          </div>
        </FiltrosResponsive>
        <Panel estado={datos}>
          {(d) => (d.filas.length === 0 ? <Vacio titulo={ficha ? 'Sin stock configurado aquí' : 'Sin resultados'}>{ficha ? 'El stock aparece al configurar el artículo en un almacén.' : null}</Vacio> : (
            <>
              <div className="tabla-desliza">
                <table aria-label="Saldos de stock" className="tabla-tarjetas">
                  <thead><tr>
                    {idArticulo === undefined && <th>Artículo</th>}
                    {idAlmacen === undefined && <th>Almacén</th>}
                    <th className="num">Saldo</th><th className="num">Mínimo</th><th className="num">Punto de pedido</th><th className="num">Máximo</th><th>Estado</th><th aria-label="Detalle" />
                  </tr></thead>
                  <tbody>
                    {d.filas.map((s) => (
                      <tr key={`${s.articulo.id}-${s.almacen.id}`}>
                        {idArticulo === undefined && <td className="celda-titulo"><Link to={`/inventario/articulos/${s.articulo.id}?tab=stock`}>{nombreArticulo(s.articulo)}</Link>{!s.articulo.activo && ' (Inactivo)'}
                          <ControlTraza a={s.articulo} /></td>}
                        {idAlmacen === undefined && <td className={idArticulo === undefined ? undefined : 'celda-titulo'} data-label={idArticulo === undefined ? 'Almacén' : undefined}>{nombreAlmacen(s.almacen)}{!s.almacen.activo && ' (Inactivo)'}</td>}
                        <td className="num" data-label="Saldo"><Cantidad v={s.saldo} u={s.unidad.codigo} /></td>
                        <td className="num" data-label="Mínimo">{s.stockMinimo ?? '—'}</td><td className="num" data-label="Punto de pedido">{s.puntoPedido ?? '—'}</td><td className="num" data-label="Máximo">{s.stockMaximo ?? '—'}</td>
                        <td data-label="Estado"><EstadoSaldo e={s.estado} />{!s.configuracionActiva && <span className="etiqueta oscura">Config. inactiva</span>}</td>
                        <td className="celda-accion">{(s.articulo.controlaLotes === true || s.articulo.controlaSeries === true) && (
                          <button type="button" className="pequeno" aria-label={`Ver detalle de ${s.articulo.codigo} en ${codigoAlmacen(s.almacen)}`}
                            onClick={() => setDetalle({ idArticulo: s.articulo.id, idAlmacen: s.almacen.id })}>
                            Ver detalle
                          </button>
                        )}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Paginacion pagina={pagina} hayMas={pagina * POR_PAGINA < d.total} fijar={setPagina} total={d.total} porPagina={POR_PAGINA} />
            </>
          ))}
        </Panel>
        {detalle !== null && <DetalleStockModal {...detalle} cerrar={() => setDetalle(null)} />}
      </div>
    </section>
  );
}

/**
 * FASE 9.4-B — Stock es la puerta de la trazabilidad: «Saldos» (artículo × almacén, con «Ver
 * detalle» por lote/serie), «Por lote» y «Por serie» (antes eran entradas propias del menú).
 */
const SUBTITULO_STOCK: Readonly<Record<string, string>> = {
  saldos: 'Saldo por artículo y almacén. El stock no se edita: cambia al confirmar operaciones en Movimientos.',
  lotes: 'Lotes de los artículos que se controlan por lote, con su caducidad y su stock. Crear un lote no crea stock: entra al confirmar una entrada con él.',
  series: 'Números de serie: dónde está cada unidad. Una serie entra en stock al confirmar una entrada y sale al confirmar una salida.',
};
export function PaginaStock(): ReactNode {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  const [nuevo, setNuevo] = useState<'lote' | 'serie' | null>(null);
  const vistas: [string, string][] = [['saldos', 'Saldos'], ...(puede('lotes:leer') ? [['lotes', 'Por lote'] as [string, string]] : []),
    ...(puede('series:leer') ? [['series', 'Por serie'] as [string, string]] : [])];
  //  F13.7: una sola pantalla de Stock con fecha. Hoy → el stock actual (saldos, lotes, series); un día anterior → el
  //  stock a esa fecha (motor de stock histórico). Al usuario no se le enseña la diferencia técnica.
  const hoy = hoyLocal();
  const historico = puede('inventario_historico:leer');
  const pedidaFecha = params.get('fecha');
  const dia = historico && pedidaFecha !== null && pedidaFecha !== '' && pedidaFecha < hoy ? pedidaFecha : hoy;
  const pasado = dia !== hoy;
  const fijarDia = (d: string): void => setParams(d === '' || d >= hoy ? {} : { fecha: d });
  const pedida = params.get('tab') ?? 'saldos';
  const tab = pasado ? 'saldos' : vistas.some(([k]) => k === pedida) ? pedida : 'saldos';
  const acciones = (
    <BarraAcciones etiqueta="Acciones de stock">
      {puede('operaciones_stock:crear') && <button className="principal" onClick={() => navegar('/inventario/movimientos?nueva=1')}>Nueva operación</button>}
      {puede('exportaciones_inventario:crear') && (
        <button onClick={() => navegar(`/inventario/exportaciones/nueva?${pasado ? `tipo=STOCK_A_FECHA&fecha=${dia}` : 'tipo=STOCK_ACTUAL'}`)}>Exportar</button>
      )}
      {tab === 'lotes' && puede('lotes:crear') && <button onClick={() => setNuevo('lote')}>Nuevo lote</button>}
      {tab === 'series' && puede('series:crear') && <button onClick={() => setNuevo('serie')}>Nueva serie</button>}
    </BarraAcciones>
  );
  return (
    <Shell dominio="movimientos" migas={[{ texto: 'Inventario' }, { texto: 'Movimientos', a: '/inventario/movimientos' }, { texto: 'Stock' }]} titulo="Stock"
      subtitulo={pasado ? 'Lo que había al final de ese día (hora de Madrid), calculado desde los movimientos.' : SUBTITULO_STOCK[tab]} acciones={acciones}
      tabs={!pasado && vistas.length > 1 ? <Pestanas opciones={vistas} actual={tab} fijar={(k) => setParams(k === 'saldos' ? {} : { tab: k })} /> : undefined}>
      {pasado && <StockAFecha dia={dia} fijarDia={fijarDia} />}
      {!pasado && tab === 'saldos' && historico && (
        <div className="panel"><div className="panel-cuerpo form-rejilla">
          <div className="campo"><label htmlFor="stock-fecha">Fecha</label>
            <input id="stock-fecha" type="date" max={hoy} value={dia} onChange={(ev) => fijarDia(ev.target.value)} />
            <span className="pequena suave">Hoy: stock actual. Elige un día anterior para ver el stock que había entonces.</span></div>
        </div></div>
      )}
      {!pasado && tab === 'saldos' && <TablaStock />}
      {!pasado && tab === 'lotes' && <ListaLotes />}
      {!pasado && tab === 'series' && <ListaSeries />}
      {nuevo === 'lote' && <NuevoLote cerrar={() => setNuevo(null)} alCrear={(l) => navegar(`/inventario/lotes/${l.id}`)} />}
      {nuevo === 'serie' && <NuevaSerie cerrar={() => setNuevo(null)} />}
    </Shell>
  );
}

// ============================================================== movimientos

function ElegirTipo({ cerrar }: { cerrar: () => void }): ReactNode {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const tipos = TIPOS_CREABLES.filter((t) => t !== 'AJUSTE' || puede('stock:ajustar'));
  return (
    <Modal titulo="Nueva operación" cerrar={cerrar}>
      <p className="suave">Elige el tipo. Se guardará como borrador: el stock no cambia hasta confirmarla.</p>
      <div className="lineas-operacion">
        {tipos.map((t) => (
          <button key={t} type="button" className="boton-tactil" style={{ textAlign: 'left' }} onClick={() => navegar(`/inventario/operaciones/nueva?tipo=${t}`)}>
            <strong>{TIPOS_OPERACION[t]}</strong><br /><span className="suave pequena">{AYUDA_TIPO[t]}</span>
          </button>
        ))}
      </div>
    </Modal>
  );
}

function ListaOperaciones(): ReactNode {
  const [q, setQ] = useState({ texto: '', tipo: '', estado: '' });
  const [pagina, setPagina] = useState(1);
  const datos = useDatos(() => {
    const p = new URLSearchParams({ pagina: String(pagina), porPagina: String(POR_PAGINA) });
    for (const [k, v] of Object.entries(q)) if (v !== '') p.set(k, v);
    return api.get<{ filas: OperacionStockResumen[]; total: number }>(`/api/inventario/operaciones?${p.toString()}`);
  }, [q.texto, q.tipo, q.estado, pagina]);
  const fijar = (k: keyof typeof q, v: string): void => { setQ((a) => ({ ...a, [k]: v })); setPagina(1); };
  return (
    <section className="panel" aria-label="Operaciones">
      <div className="panel-cuerpo">
        <FiltrosResponsive contador={[q.tipo, q.estado].filter((x) => x !== '').length}
          busqueda={<input type="search" aria-label="Buscar operación" placeholder="Código o documento…" title="Busca en el código y en el documento (tipo y número)" value={q.texto} onChange={(ev) => fijar('texto', ev.target.value)} />}>
          <div className="filtros-rejilla">
            <label className="campo"><span>Tipo</span>
              <select value={q.tipo} onChange={(ev) => fijar('tipo', ev.target.value)}>
                <option value="">Todos</option>
                {(Object.keys(TIPOS_OPERACION) as TipoOperacionStock[]).map((t) => <option key={t} value={t}>{TIPOS_OPERACION[t]}</option>)}
              </select>
            </label>
            <label className="campo"><span>Estado</span>
              <select value={q.estado} onChange={(ev) => fijar('estado', ev.target.value)}>
                <option value="">Todos</option>
                {(Object.keys(ESTADOS_OPERACION) as EstadoOperacionStock[]).map((e) => <option key={e} value={e}>{ESTADOS_OPERACION[e][0]}</option>)}
              </select>
            </label>
          </div>
        </FiltrosResponsive>
        <Panel estado={datos}>
          {(d) => (d.filas.length === 0 ? (Object.values(q).some((x) => x !== '')
            ? <SinResultados limpiar={() => { setQ({ texto: '', tipo: '', estado: '' }); setPagina(1); }} />
            : <EmptyState titulo="Sin operaciones" descripcion="Las entradas, salidas, traspasos y ajustes aparecerán aquí." />) : (
            <>
              <div className="tabla-desliza">
                <table aria-label="Operaciones de stock" className="tabla-tarjetas">
                  <thead><tr><th>Código</th><th>Tipo</th><th>Almacén</th><th>Documento</th><th className="num">Líneas</th><th>Estado</th><th>Creada</th></tr></thead>
                  <tbody>
                    {d.filas.map((o) => (
                      <tr key={o.id}>
                        <td className="mono celda-titulo"><Link to={`/inventario/operaciones/${o.id}`}>{o.codigo}</Link></td>
                        {/* UX-1D: el PT de un consumo/devolución enlaza a su parte (antes, texto plano). */}
                        <td data-label="Tipo">{TIPOS_OPERACION[o.tipo]}{o.origen !== null && <span className="suave pequena"> · rectifica {o.origen.codigo}</span>}
                          {o.parte != null && <span className="pequena"> · <Link className="mono" to={`/mantenimiento/partes/${o.parte.id}#materiales`}>{o.parte.codigo}</Link></span>}
                          {o.ot != null && <span className="pequena"> · <Link className="mono" to={`/ots/${o.ot.id}#consumos`}>{o.ot.codigo}</Link></span>}</td>
                        <td data-label="Almacén">{codigoAlmacen(o.almacen)}{o.almacenDestino !== null && ` → ${codigoAlmacen(o.almacenDestino)}`}</td>
                        <td data-label="Documento">{[o.tipoDocumento, o.numeroDocumento].filter(Boolean).join(' ') || '—'}</td>
                        <td className="num" data-label="Líneas">{o.nLineas}</td>
                        <td data-label="Estado"><EstadoOperacion e={o.estado} /></td>
                        <td className="pequena" data-label="Creada">{o.creadoPor.nombre} · {fechaHora(o.creadoEn)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Paginacion pagina={pagina} hayMas={pagina * POR_PAGINA < d.total} fijar={setPagina} total={d.total} porPagina={POR_PAGINA} />
            </>
          ))}
        </Panel>
      </div>
    </section>
  );
}

/** Histórico (ledger): movimientos confirmados, del más reciente al más antiguo. */
export function Ledger({ idArticulo, idAlmacen, idLote, idSerie }: { idArticulo?: string; idAlmacen?: string; idLote?: string; idSerie?: string }): ReactNode {
  const [q, setQ] = useState({ sentido: '', tipo: '', desde: '', hasta: '' });
  const [pagina, setPagina] = useState(1);
  const datos = useDatos(() => {
    const p = new URLSearchParams({ pagina: String(pagina), porPagina: String(POR_PAGINA) });
    for (const [k, v] of Object.entries(q)) if (v !== '') p.set(k, v);
    if (idArticulo !== undefined) p.set('idArticulo', idArticulo);
    if (idAlmacen !== undefined) p.set('idAlmacen', idAlmacen);
    if (idLote !== undefined) p.set('idLote', idLote);
    if (idSerie !== undefined) p.set('idSerie', idSerie);
    return api.get<{ filas: MovimientoStock[]; hayMas: boolean }>(`/api/inventario/movimientos?${p.toString()}`);
  }, [q.sentido, q.tipo, q.desde, q.hasta, idArticulo, idAlmacen, idLote, idSerie, pagina]);
  const fijar = (k: keyof typeof q, v: string): void => { setQ((a) => ({ ...a, [k]: v })); setPagina(1); };
  return (
    <section className="panel" aria-label="Histórico">
      <div className="panel-cuerpo">
        <p className="suave pequena nota-ledger">Histórico de entradas y salidas confirmadas. No se edita.</p>
        <FiltrosResponsive contador={Object.values(q).filter((x) => x !== '').length}>
          <div className="filtros-rejilla">
            <label className="campo"><span>Sentido</span>
              <select value={q.sentido} onChange={(ev) => fijar('sentido', ev.target.value)}>
                <option value="">Todos</option><option value="ENTRADA">Entrada</option><option value="SALIDA">Salida</option>
              </select>
            </label>
            <label className="campo"><span>Tipo de operación</span>
              <select value={q.tipo} onChange={(ev) => fijar('tipo', ev.target.value)}>
                <option value="">Todos</option>
                {(Object.keys(TIPOS_OPERACION) as TipoOperacionStock[]).map((t) => <option key={t} value={t}>{TIPOS_OPERACION[t]}</option>)}
              </select>
            </label>
            <label className="campo"><span>Desde</span><input type="date" value={q.desde} onChange={(ev) => fijar('desde', ev.target.value)} /></label>
            <label className="campo"><span>Hasta</span><input type="date" value={q.hasta} onChange={(ev) => fijar('hasta', ev.target.value)} /></label>
          </div>
        </FiltrosResponsive>
        <Panel estado={datos}>
          {(d) => {
            //  Lote / serie sólo si alguna fila los tiene (artículos sin trazabilidad: tabla de siempre).
            const traza = d.filas.some((m) => (m.lote ?? null) !== null || (m.serie ?? null) !== null) && idSerie === undefined;
            return d.filas.length === 0 ? (Object.values(q).some((x) => x !== '')
              ? <SinResultados limpiar={() => { setQ({ sentido: '', tipo: '', desde: '', hasta: '' }); setPagina(1); }} />
              : <EmptyState titulo="Sin movimientos confirmados" descripcion="Cada operación confirmada deja aquí su entrada o salida." />) : (
            <>
              <div className="tabla-desliza">
                <table aria-label="Movimientos de stock" className="tabla-tarjetas">
                  <thead><tr><th>Fecha</th><th>Operación</th>{idArticulo === undefined && idLote === undefined && idSerie === undefined && <th>Artículo</th>}{idAlmacen === undefined && <th>Almacén</th>}
                    {traza && <th>Lote / serie</th>}<th>Sentido</th><th>Cantidad</th><th>Origen</th><th>Usuario</th></tr></thead>
                  <tbody>
                    {d.filas.map((m) => (
                      <tr key={m.id}>
                        <td className="pequena" data-label="Fecha">{fechaHora(m.creadoEn)}</td>
                        <td className="celda-titulo">{m.operacion.visible ? <Link className="mono" to={`/inventario/operaciones/${m.operacion.id}`}>{m.operacion.codigo}</Link> : <span className="mono">{m.operacion.codigo}</span>}
                          <span className="suave pequena"> · {TIPOS_OPERACION[m.operacion.tipo]}</span>
                          {m.operacion.parte != null && <span className="pequena"> · <Link className="mono" to={`/mantenimiento/partes/${m.operacion.parte.id}`}>{m.operacion.parte.codigo}</Link></span>}
                          {m.operacion.ot != null && <span className="pequena"> · <Link className="mono" to={`/ots/${m.operacion.ot.id}${m.operacion.ot.idConsumo === null ? '#consumos' : `#consumo-${m.operacion.ot.idConsumo}`}`}>{m.operacion.ot.codigo}</Link></span>}</td>
                        {idArticulo === undefined && idLote === undefined && idSerie === undefined && <td data-label="Artículo">{nombreArticulo(m.articulo)}</td>}
                        {idAlmacen === undefined && <td data-label="Almacén">{codigoAlmacen(m.almacen)}</td>}
                        {traza && <td className="pequena mono" data-label="Lote / serie">{m.lote != null && idLote === undefined && <Link to={`/inventario/lotes/${m.lote.id}`}>{m.lote.codigo}</Link>}
                          {m.lote != null && m.serie != null && idLote === undefined && ' · '}{m.serie != null && <Link to={`/inventario/series/${m.serie.id}`}>{m.serie.numero}</Link>}
                          {m.lote == null && m.serie == null && '—'}</td>}
                        {/* UX-1D: el sentido es DIRECCIÓN, no estado: chip neutro con flecha (antes la salida usaba el color de aviso). */}
                        <td data-label="Sentido"><span className={`etiqueta sentido sentido-${m.sentido === 'ENTRADA' ? 'entrada' : 'salida'}`}>{m.sentido === 'ENTRADA' ? 'Entrada' : 'Salida'}</span></td>
                        <td data-label="Cantidad"><Cantidad v={`${m.sentido === 'ENTRADA' ? '+' : '−'}${m.cantidadStock}`} u={m.unidadStock} /></td>
                        <td className="pequena" data-label="Origen">{m.unidadOrigen === m.unidadStock ? '—' : `${m.cantidadOrigen} ${m.unidadOrigen} × ${m.factorConversion}`}</td>
                        <td className="pequena" data-label="Usuario">{m.creadoPor.nombre}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Paginacion pagina={pagina} hayMas={d.hayMas} fijar={setPagina} />
            </>
            );
          }}
        </Panel>
      </div>
    </section>
  );
}

export function PaginaMovimientos(): ReactNode {
  const { puede } = useSesion();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'operaciones';
  const nueva = params.get('nueva') === '1';
  const navegar = useNavigate();
  //  F13.7: exportar es una ACCIÓN de Movimientos (y de Stock y de cada Cierre), no una entrada del menú.
  const acciones = (
    <BarraAcciones etiqueta="Acciones de movimientos">
      {puede('operaciones_stock:crear') && <button className="principal" onClick={() => setParams({ tab, nueva: '1' })}>Nueva operación</button>}
      {puede('exportaciones_inventario:crear') && <button onClick={() => navegar('/inventario/exportaciones/nueva?tipo=MOVIMIENTOS')}>Exportar</button>}
      {puede('exportaciones_inventario:leer') && <Link className="boton" to="/inventario/exportaciones">Historial de exportaciones</Link>}
    </BarraAcciones>
  );
  return (
    <Shell dominio="movimientos" migas={[{ texto: 'Inventario' }, { texto: 'Movimientos' }]} titulo="Movimientos"
      acciones={acciones}
      tabs={<Pestanas opciones={[['operaciones', 'Operaciones'], ['ledger', 'Histórico']]} actual={tab} fijar={(k) => setParams({ tab: k })} />}>
      {tab === 'operaciones' ? <ListaOperaciones /> : <Ledger />}
      {nueva && <ElegirTipo cerrar={() => setParams({ tab })} />}
    </Shell>
  );
}

// ======================================================== nueva operación

interface LineaForm {
  clave: number; articulo: OpcionArticuloStock | null; idUnidad: string; cantidad: string; sentido: '' | SentidoStock;
  /** FASE 9.4. */
  lote: OpcionLote | null; series: string;
}
let siguienteClave = 1;
const lineaVacia = (): LineaForm => ({ clave: siguienteClave++, articulo: null, idUnidad: '', cantidad: '', sentido: '', lote: null, series: '' });
const serializada = (l: LineaForm): boolean => l.articulo?.trazabilidad?.controlaSeries === true;
/** Cuerpo de la línea para la API: un artículo serializado no manda cantidad ni unidad (son sus series). */
function cuerpoLinea(l: LineaForm, tipo: TipoOperacionStock): Record<string, unknown> {
  const traz = l.articulo?.trazabilidad;
  return {
    idArticulo: l.articulo?.id ?? null,
    ...(serializada(l) ? { series: seriesDe(l.series) } : { idUnidad: l.idUnidad || null, cantidad: l.cantidad }),
    ...(traz?.controlaLotes === true ? { idLote: l.lote?.id ?? null } : {}),
    ...(tipo === 'AJUSTE' ? { sentido: l.sentido || null } : {}),
  };
}
const lineaLista = (l: LineaForm): boolean => l.articulo !== null && (serializada(l) ? seriesDe(l.series).length > 0 : l.cantidad !== '');

/** Cantidad en unidad de stock (informativa) y aviso si una salida supera el saldo. */
/**
 * Saldo informativo de la línea (a todo el ancho, bajo los campos). Con lote, el del LOTE en
 * este almacén; un lote recién creado está a 0 y se explica por qué (crear un lote no crea stock).
 */
function SaldoInformativo({ l, resta }: { l: LineaForm; resta: boolean }): ReactNode {
  if (l.articulo === null) return null;
  const u = l.articulo.unidades.find((x) => x.id === l.idUnidad);
  const conLotes = l.articulo.trazabilidad?.controlaLotes === true;
  const ud = l.articulo.unidadStock.codigo;
  if (conLotes && l.lote === null) {
    return <p className="ayuda-linea suave pequena" aria-live="polite">Saldo del artículo aquí: <strong>{l.articulo.saldo ?? '0'} {ud}</strong>, repartido por lotes. Elige el lote de esta línea.</p>;
  }
  const saldo = l.lote !== null ? (l.lote.saldo ?? '0') : (l.articulo.saldo ?? '0');
  const n = serializada(l) ? seriesDe(l.series).length : Number(l.cantidad.replace(',', '.'));
  const enStock = u !== undefined && Number.isFinite(n) && n > 0 ? n * Number(u.factor) : null;
  const loteVacio = l.lote !== null && Number(saldo) === 0;
  return (
    <p className="ayuda-linea suave pequena" aria-live="polite">
      {l.lote !== null ? <>Saldo actual del lote {l.lote.codigo} en este almacén: </> : <>Saldo actual: </>}
      <strong>{saldo} {ud}</strong> (informativo)
      {enStock !== null && u !== undefined && u.id !== l.articulo.unidadStock.id && ` · ${l.cantidad} ${u.codigo} = ${String(Number(enStock.toFixed(6)))} ${ud}`}
      {loteVacio && !resta && <> · Es normal en un lote sin entradas: tendrá stock al <strong>confirmar</strong> esta operación.</>}
      {resta && enStock !== null && enStock > Number(saldo) && <span className="saldo-negativo"> · Supera el saldo actual: se comprobará al confirmar.</span>}
    </p>
  );
}

/** Cómo se registra esta línea según el control del artículo (una frase, no un manual). */
function GuiaTraza({ l, resta }: { l: LineaForm; resta: boolean }): ReactNode {
  const t = l.articulo?.trazabilidad;
  if (t === undefined || (!t.controlaLotes && !t.controlaSeries)) return null;
  const partes = [
    t.controlaLotes && (resta ? 'elige el lote del que sale (sólo lotes con stock aquí)' : 'elige el lote o créalo con «Nuevo lote»'),
    t.controlaSeries && (resta ? 'indica las series que salen (deben estar en stock aquí)' : 'teclea, pega o escanea sus números de serie (1 serie = 1 unidad)'),
  ].filter(Boolean) as string[];
  return <p className="guia-traza pequena">Este artículo {t.controlaLotes ? 'se controla por lotes' : ''}{t.controlaLotes && t.controlaSeries ? ' y ' : ''}{t.controlaSeries ? 'lleva números de serie' : ''}: {partes.join('; ')}. El stock cambia al confirmar.</p>;
}

function EditorLinea({ l, i, tipo, idAlmacen, campos, cambiar, quitar }: {
  l: LineaForm; i: number; tipo: TipoOperacionStock; idAlmacen: string | null; campos: Record<string, string>;
  cambiar: (l: LineaForm) => void; quitar: (() => void) | null;
}): ReactNode {
  const pref = `lineas.${String(i)}.`;
  const resta = tipo === 'SALIDA' || tipo === 'TRASLADO' || (tipo === 'AJUSTE' && l.sentido === 'SALIDA');
  return (
    <fieldset className="linea-operacion" aria-label={`Línea ${String(i + 1)}`}>
      <legend className="pequena suave">Línea {i + 1}</legend>
      {/* FASE 9.4-B: rejilla propia de la línea, alineada por arriba: Artículo y Lote ocupan dos
          columnas cada uno; Unidad, Cantidad y Sentido, una. Series, avisos y saldo, debajo a todo el ancho. */}
      <div className="linea-rejilla">
        <div className="linea-celda ancho-2">
          <Buscador<OpcionArticuloStock> etiqueta="Artículo" valor={l.articulo} contexto={idAlmacen ?? ''}
            deshabilitado={idAlmacen === null} placeholder={idAlmacen === null ? 'Elige antes el almacén' : 'Código o descripción…'}
            sinResultados="Ningún artículo activo configurado en este almacén."
            buscar={buscarArticulos(idAlmacen)} clave={(a) => a.id} texto={nombreArticulo}
            detalle={(a) => <>{`Saldo ${a.saldo ?? '0'} ${a.unidadStock.codigo}`}<ControlTraza a={a.trazabilidad ?? {}} /></>}
            alElegir={(a) => cambiar({ ...l, articulo: a, idUnidad: a?.unidadStock.id ?? '', lote: null, series: '' })}
            error={campos[`${pref}idArticulo`]} />
        </div>
        {l.articulo?.trazabilidad?.controlaLotes === true && idAlmacen !== null && (
          <div className="linea-celda ancho-2">
            <SelectorLote articulo={{ ...l.articulo, controlaCaducidad: l.articulo.trazabilidad.controlaCaducidad }} idAlmacen={idAlmacen} conStock={resta}
              valor={l.lote} alElegir={(lo) => cambiar({ ...l, lote: lo, series: resta ? '' : l.series })} error={campos[`${pref}idLote`]} />
          </div>
        )}
        {serializada(l) ? null : <>
        <div className="campo">
          <label htmlFor={`l${String(l.clave)}-u`}>Unidad</label>
          <select id={`l${String(l.clave)}-u`} value={l.idUnidad} disabled={l.articulo === null} onChange={(ev) => cambiar({ ...l, idUnidad: ev.target.value })}>
            {(l.articulo?.unidades ?? []).map((u) => <option key={u.id} value={u.id}>{u.codigo}{u.factor === '1' ? '' : ` (= ${u.factor} ${l.articulo!.unidadStock.codigo})`}</option>)}
          </select>
          {campos[`${pref}idUnidad`] !== undefined && <div className="error-campo">{campos[`${pref}idUnidad`]}</div>}
        </div>
        <div className="campo">
          <label htmlFor={`l${String(l.clave)}-c`}>Cantidad</label>
          <input id={`l${String(l.clave)}-c`} inputMode="decimal" value={l.cantidad} onChange={(ev) => cambiar({ ...l, cantidad: ev.target.value })} />
          {campos[`${pref}cantidad`] !== undefined && <div className="error-campo">{campos[`${pref}cantidad`]}</div>}
        </div>
        </>}
        {tipo === 'AJUSTE' && (
          <div className="campo">
            <label htmlFor={`l${String(l.clave)}-s`}>Sentido</label>
            <select id={`l${String(l.clave)}-s`} value={l.sentido} onChange={(ev) => cambiar({ ...l, sentido: ev.target.value as LineaForm['sentido'] })}>
              <option value="">Elige…</option><option value="ENTRADA">Suma (entrada)</option><option value="SALIDA">Resta (salida)</option>
            </select>
            {campos[`${pref}sentido`] !== undefined && <div className="error-campo">{campos[`${pref}sentido`]}</div>}
          </div>
        )}
      </div>
      <GuiaTraza l={l} resta={resta} />
      {serializada(l) && l.articulo !== null && idAlmacen !== null && (
          <EditorSeries id={`l${String(l.clave)}-series`} valor={l.series} cambiar={(v) => cambiar({ ...l, series: v })}
            etiqueta={`Números de serie (1 serie = 1 ${l.articulo.unidadStock.codigo})`}
            error={campos[`${pref}series`] ?? campos[`${pref}cantidad`]}
            disponibles={resta && (l.articulo.trazabilidad?.controlaLotes !== true || l.lote !== null)
              ? buscarSeriesDisponibles(l.articulo.id, idAlmacen, l.lote?.id ?? null) : undefined} />
      )}
      <SaldoInformativo l={l} resta={resta} />
      {quitar !== null && <div className="linea-pie"><button type="button" className="pequeno" onClick={quitar}>Quitar línea</button></div>}
    </fieldset>
  );
}

/**
 * F13.3 — una lectura del escáner en las líneas del borrador: fungible repetido SUMA en su línea
 * (mismo artículo, lote y unidad de stock); serie repetida es error; serializado nuevo añade su serie.
 * Nada se confirma: el stock cambia sólo al confirmar la operación.
 */
export async function lecturaEnLineas(lineas: LineaForm[], l: Lectura, tipo: TipoOperacionStock, idAlmacen: string): Promise<{ lineas: LineaForm[] } | { error: string }> {
  const r = l.recurso;
  if (r.tipo === 'ALMACEN') return { error: `Es la etiqueta del almacén ${r.almacen === null ? '' : codigoAlmacen(r.almacen)}: el almacén se elige arriba.` };
  if (r.articulo === null) return { error: 'La lectura no identifica un artículo.' };
  const art = await opcionArticulo(r.articulo.id, r.articulo.codigo, idAlmacen);
  if (art === null) return { error: `${r.articulo.codigoNegocio} no está configurado (activo e inventariable) en este almacén.` };
  const resta = tipo === 'SALIDA' || tipo === 'TRASLADO';
  let lote = null as LineaForm['lote'];
  if (r.lote !== null) {
    if (resta && r.lote.estado === 'BLOQUEADO') return { error: `El lote ${r.lote.codigo} está bloqueado: no puede salir ni trasladarse.` };
    lote = await opcionLote(art.id, r.lote.id, r.lote.codigo, idAlmacen);
    if (lote === null) return { error: `El lote ${r.lote.codigo} no está disponible.` };
  }
  const vacias = (x: LineaForm): boolean => x.articulo === null && x.cantidad === '' && x.series === '';
  const base = lineas.filter((x) => !vacias(x));
  const misma = base.find((x) => x.articulo?.id === art.id && (x.lote?.id ?? null) === (lote?.id ?? null)
    && (art.trazabilidad?.controlaSeries === true || x.idUnidad === art.unidadStock.id));
  if (art.trazabilidad?.controlaSeries === true) {
    const numero = r.serie?.numero ?? l.seriePendiente;
    if (numero == null) {
      return misma !== undefined ? { lineas } : { lineas: [...base, { ...lineaVacia(), articulo: art, idUnidad: art.unidadStock.id, lote }] };
    }
    if (misma !== undefined) {
      const n = anadirSerie(misma.series, numero);
      if (n === null) return { error: `La serie ${numero} ya está en esta operación.` };
      return { lineas: base.map((x) => (x.clave === misma.clave ? { ...x, series: n } : x)) };
    }
    if (base.some((x) => x.articulo?.id === art.id && anadirSerie(x.series, numero) === null)) return { error: `La serie ${numero} ya está en esta operación.` };
    return { lineas: [...base, { ...lineaVacia(), articulo: art, idUnidad: art.unidadStock.id, lote, series: numero }] };
  }
  const cantidad = l.cantidad ?? 1;
  if (misma !== undefined) return { lineas: base.map((x) => (x.clave === misma.clave ? { ...x, cantidad: sumar(x.cantidad, cantidad) } : x)) };
  return { lineas: [...base, { ...lineaVacia(), articulo: art, idUnidad: art.unidadStock.id, lote, cantidad: String(cantidad) }] };
}
const USO_DE_TIPO: Record<TipoOperacionStock, UsoEscaneo> = {
  ENTRADA: 'ENTRADA', SALIDA: 'SALIDA', TRASLADO: 'TRASLADO', AJUSTE: 'AJUSTE', DEVOLUCION: 'DEVOLUCION', CONSUMO_PT: 'CONSUMO_PT', CONSUMO_OT: 'CONSUMO_OT',
};

export function PaginaNuevaOperacion(): ReactNode {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const [params] = useSearchParams();
  const tipo = (params.get('tipo') ?? 'ENTRADA') as TipoOperacionStock;
  const [almacen, setAlmacen] = useState<OpcionAlmacenStock | null>(null);
  const [destino, setDestino] = useState<OpcionAlmacenStock | null>(null);
  //  F13.4: «Cargar / Devolver» desde la ficha de una furgoneta llega con el origen o el destino ya elegidos.
  useEffect(() => {
    const precargar = async (id: string | null, codigo: string | null, fijar: (a: OpcionAlmacenStock) => void): Promise<void> => {
      if (id === null || codigo === null) return;
      const a = (await buscarAlmacenes(codigo)).find((x) => x.id === id);
      if (a !== undefined) fijar(a);
    };
    void precargar(params.get('origen'), params.get('origenCodigo'), setAlmacen).catch(() => undefined);
    void precargar(params.get('destino'), params.get('destinoCodigo'), setDestino).catch(() => undefined);
  }, []);
  const [doc, setDoc] = useState({ tipoDocumento: '', numeroDocumento: '', fechaDocumento: '', observaciones: '', motivo: '' });
  const [lineas, setLineas] = useState<LineaForm[]>([lineaVacia()]);
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const campos = erroresDe(error);
  const migas = [{ texto: 'Inventario' }, { texto: 'Movimientos', a: '/inventario/movimientos' }, { texto: 'Nueva' }];
  if (!TIPOS_CREABLES.includes(tipo) || !puede('operaciones_stock:crear') || (tipo === 'AJUSTE' && !puede('stock:ajustar'))) {
    return <Shell migas={migas} titulo="Operación no disponible"><div className="panel"><Vacio titulo="No puedes crear este tipo de operación"><Link to="/inventario/movimientos">Volver</Link></Vacio></div></Shell>;
  }
  const traslado = tipo === 'TRASLADO';
  const cambiarAlmacen = (a: OpcionAlmacenStock | null): void => {
    setAlmacen(a);
    //  Los artículos dependen del almacén: las líneas ya elegidas se vacían.
    setLineas((ls) => ls.map((l) => ({ ...l, articulo: null, idUnidad: '', lote: null, series: '' })));
  };
  async function guardar(): Promise<void> {
    setError(null);
    setEnviando(true);
    try {
      const op = await api.post<OperacionStockDetalle>('/api/inventario/operaciones', {
        tipo, idAlmacen: almacen?.id ?? null, ...(traslado ? { idAlmacenDestino: destino?.id ?? null } : {}),
        tipoDocumento: doc.tipoDocumento, numeroDocumento: doc.numeroDocumento, fechaDocumento: doc.fechaDocumento === '' ? null : doc.fechaDocumento,
        observaciones: doc.observaciones,
        ...(tipo === 'AJUSTE' ? { motivo: doc.motivo } : {}),
        lineas: lineas.filter((l) => l.articulo !== null || l.cantidad !== '').map((l) => cuerpoLinea(l, tipo)),
      });
      navegar(`/inventario/operaciones/${op.id}`);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  return (
    <Shell dominio="movimientos" migas={migas} titulo={`Nueva ${TIPOS_OPERACION[tipo].toLowerCase()}`} subtitulo={AYUDA_TIPO[tipo]}>
      <form className="panel" aria-label="Nueva operación" onSubmit={(ev) => { ev.preventDefault(); void guardar(); }}>
        <div className="panel-cuerpo">
          <p className="nota aviso banner-borrador" role="note">Se guardará como <strong>borrador</strong>. {TEXTO_BORRADOR}</p>
          {error !== null && <ErrorStock error={error} />}
          <div className="form-rejilla">
            <Buscador<OpcionAlmacenStock> etiqueta={traslado ? 'Almacén de origen' : 'Almacén'} valor={almacen} alElegir={cambiarAlmacen}
              buscar={buscarAlmacenes} clave={(a) => a.id} texto={nombreAlmacen} error={campos['idAlmacen']} />
            {traslado && (
              <Buscador<OpcionAlmacenStock> etiqueta="Almacén de destino" valor={destino} alElegir={setDestino}
                buscar={async (t) => (await buscarAlmacenes(t)).filter((a) => a.id !== almacen?.id)} contexto={almacen?.id ?? ''}
                clave={(a) => a.id} texto={nombreAlmacen} error={campos['idAlmacenDestino']} />
            )}
            {tipo !== 'AJUSTE' && (
              <>
                <div className="campo"><label htmlFor="op-tdoc">Tipo de documento</label>
                  <input id="op-tdoc" value={doc.tipoDocumento} maxLength={40} placeholder="Albarán, vale…" onChange={(ev) => setDoc({ ...doc, tipoDocumento: ev.target.value })} /></div>
                <div className="campo"><label htmlFor="op-ndoc">Nº de documento</label>
                  <input id="op-ndoc" value={doc.numeroDocumento} maxLength={80} onChange={(ev) => setDoc({ ...doc, numeroDocumento: ev.target.value })} /></div>
                <div className="campo"><label htmlFor="op-fdoc">Fecha del documento</label>
                  <input id="op-fdoc" type="date" value={doc.fechaDocumento} max={hoyLocal()} onChange={(ev) => setDoc({ ...doc, fechaDocumento: ev.target.value })} />
                  {campos['fechaDocumento'] !== undefined && <div className="error-campo">{campos['fechaDocumento']}</div>}</div>
              </>
            )}
          </div>
          {tipo === 'AJUSTE' && (
            <div className="campo"><label htmlFor="op-motivo">Motivo del ajuste (obligatorio)</label>
              <textarea id="op-motivo" rows={2} value={doc.motivo} maxLength={2000} onChange={(ev) => setDoc({ ...doc, motivo: ev.target.value })} />
              {campos['motivo'] !== undefined && <div className="error-campo">{campos['motivo']}</div>}</div>
          )}
          <div className="campo"><label htmlFor="op-obs">Observaciones</label>
            <textarea id="op-obs" rows={2} value={doc.observaciones} maxLength={2000} onChange={(ev) => setDoc({ ...doc, observaciones: ev.target.value })} /></div>
          <h3>Líneas</h3>
          {traslado && almacen !== null && destino === null && <p className="suave pequena">Los artículos deben estar configurados también en el almacén de destino.</p>}
          <Escaner titulo="Escanear artículos" contexto={{ uso: USO_DE_TIPO[tipo], idAlmacen: almacen?.id ?? null }}
            deshabilitado={almacen === null ? 'Elige antes el almacén para escanear.' : null}
            alLeer={async (l) => {
              if (almacen === null) return 'Elige antes el almacén.';
              const r = await lecturaEnLineas(lineas, l, tipo, almacen.id);
              if ('error' in r) return r.error;
              setLineas(r.lineas);
              return null;
            }} />
          <div className="lineas-operacion">
            {lineas.map((l, i) => (
              <EditorLinea key={l.clave} l={l} i={i} tipo={tipo} idAlmacen={almacen?.id ?? null} campos={campos}
                cambiar={(n) => setLineas((ls) => ls.map((x) => (x.clave === l.clave ? n : x)))}
                quitar={lineas.length > 1 ? () => setLineas((ls) => ls.filter((x) => x.clave !== l.clave)) : null} />
            ))}
          </div>
          <div className="fila" style={{ marginTop: 12, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setLineas((ls) => [...ls, lineaVacia()])}>Añadir línea</button>
            <span style={{ flex: 1 }} />
            <Link to="/inventario/movimientos" className="boton">Cancelar</Link>
            <button type="submit" className="principal" disabled={enviando || almacen === null}>Guardar borrador</button>
          </div>
        </div>
      </form>
    </Shell>
  );
}

// ========================================================== ficha operación

function AnadirLinea({ op, alCambiar }: { op: OperacionStockDetalle; alCambiar: () => void }): ReactNode {
  const [l, setL] = useState<LineaForm>(lineaVacia());
  const [error, setError] = useState<unknown>(null);
  async function anadir(): Promise<void> {
    setError(null);
    try {
      await api.post(`/api/inventario/operaciones/${op.id}/lineas`, cuerpoLinea(l, op.tipo));
      setL(lineaVacia());
      alCambiar();
    } catch (e) {
      setError(e);
    }
  }
  const campos = Object.fromEntries(Object.entries(erroresDe(error)).map(([k, v]) => [`lineas.0.${k}`, v]));
  return (
    <form aria-label="Añadir línea" onSubmit={(ev) => { ev.preventDefault(); void anadir(); }} style={{ marginTop: 12 }}>
      {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
      <EditorLinea l={l} i={0} tipo={op.tipo} idAlmacen={op.almacen.id} campos={campos} cambiar={setL} quitar={null} />
      <div className="fila" style={{ marginTop: 8 }}><button type="submit" disabled={!lineaLista(l)}>Añadir línea</button></div>
    </form>
  );
}

function EditarLinea({ op, l, cerrar, alCambiar }: { op: OperacionStockDetalle; l: LineaOperacionStock; cerrar: () => void; alCambiar: () => void }): ReactNode {
  const [cantidad, setCantidad] = useState(l.cantidad);
  const [sentido, setSentido] = useState<SentidoStock | ''>(l.sentido ?? '');
  const serie = l.articulo.controlaSeries === true;
  const [series, setSeries] = useState((l.series ?? []).map((x) => x.numero).join('\n'));
  const [error, setError] = useState<unknown>(null);
  const campos = erroresDe(error);
  const resta = op.tipo === 'SALIDA' || op.tipo === 'TRASLADO' || (op.tipo === 'AJUSTE' && sentido === 'SALIDA');
  async function guardar(): Promise<void> {
    setError(null);
    try {
      await api.patch(`/api/inventario/operaciones/${op.id}/lineas/${l.id}`, {
        ...(serie ? { series: seriesDe(series) } : { cantidad }), versionEsperada: l.actualizadoEn, ...(op.tipo === 'AJUSTE' ? { sentido: sentido || null } : {}),
      });
      alCambiar();
      cerrar();
    } catch (e) {
      setError(e);
    }
  }
  return (
    <Modal titulo={`Editar línea ${String(l.orden)}`} cerrar={cerrar}
      pie={<><button onClick={cerrar}>Cancelar</button><button className="principal" onClick={() => void guardar()}>Guardar</button></>}>
      <p>{nombreArticulo(l.articulo)}</p>
      <TrazaLinea lote={l.lote} />
      {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
      {serie
        ? (
            <EditorSeries id="el-series" valor={series} cambiar={setSeries} error={campos['series'] ?? campos['cantidad']}
              disponibles={resta ? buscarSeriesDisponibles(l.articulo.id, op.almacen.id, l.lote?.id ?? null) : undefined} />
        )
        : (
          <div className="campo"><label htmlFor="el-c">Cantidad ({l.unidad.codigo})</label>
            <input id="el-c" inputMode="decimal" value={cantidad} onChange={(ev) => setCantidad(ev.target.value)} />
            {campos['cantidad'] !== undefined && <div className="error-campo">{campos['cantidad']}</div>}</div>
        )}
      {op.tipo === 'AJUSTE' && (
        <div className="campo"><label htmlFor="el-s">Sentido</label>
          <select id="el-s" value={sentido} onChange={(ev) => setSentido(ev.target.value as SentidoStock)}>
            <option value="ENTRADA">Suma (entrada)</option><option value="SALIDA">Resta (salida)</option>
          </select></div>
      )}
    </Modal>
  );
}

/**
 * Cabecera editable de un BORRADOR (FASE 9.2-C): documento, motivo y
 * observaciones. El tipo y los almacenes NO se cambian aquí (si el tipo era
 * otro, se anula el borrador y se crea uno nuevo). PATCH validado por la API,
 * con `versionEsperada`: si otra persona lo cambió antes, 409 y se recarga.
 */
function CabeceraBorrador({ op, alGuardar }: { op: OperacionStockDetalle; alGuardar: () => void }): ReactNode {
  const inicial = { tipoDocumento: op.tipoDocumento ?? '', numeroDocumento: op.numeroDocumento ?? '', fechaDocumento: op.fechaDocumento ?? '',
    motivo: op.motivo ?? '', observaciones: op.observaciones ?? '' };
  const [v, setV] = useState(inicial);
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);
  const campos = erroresDe(error);
  const cambiado = (Object.keys(inicial) as (keyof typeof inicial)[]).some((k) => v[k].trim() !== inicial[k]);
  async function guardar(): Promise<void> {
    setError(null);
    setGuardando(true);
    try {
      //  F13.5-B: la fecha del documento sólo viaja si cambia (vacía = quitarla).
      const { fechaDocumento, ...resto } = v;
      await api.patch(`/api/inventario/operaciones/${op.id}`, {
        ...resto, ...(fechaDocumento !== inicial.fechaDocumento ? { fechaDocumento: fechaDocumento === '' ? null : fechaDocumento } : {}),
        versionEsperada: op.actualizadoEn,
      });
      alGuardar();
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }
  const campo = (k: keyof typeof inicial, etiqueta: string, max: number, area = false): ReactNode => (
    <div className="campo">
      <label htmlFor={`cab-${k}`}>{etiqueta}</label>
      {area
        ? <textarea id={`cab-${k}`} rows={2} maxLength={max} value={v[k]} onChange={(ev) => setV({ ...v, [k]: ev.target.value })} />
        : <input id={`cab-${k}`} maxLength={max} value={v[k]} onChange={(ev) => setV({ ...v, [k]: ev.target.value })} />}
      {campos[k] !== undefined && <div className="error-campo">{campos[k]}</div>}
    </div>
  );
  return (
    <form aria-label="Editar información de la operación" className="cabecera-borrador" onSubmit={(ev) => { ev.preventDefault(); void guardar(); }}>
      {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
      <div className="form-rejilla">
        {campo('tipoDocumento', 'Tipo de documento', 40)}
        {campo('numeroDocumento', 'Nº de documento', 80)}
        <div className="campo">
          <label htmlFor="cab-fechaDocumento">Fecha del documento</label>
          <input id="cab-fechaDocumento" type="date" max={hoyLocal()} value={v.fechaDocumento} onChange={(ev) => setV({ ...v, fechaDocumento: ev.target.value })} />
          {campos['fechaDocumento'] !== undefined && <div className="error-campo">{campos['fechaDocumento']}</div>}
        </div>
      </div>
      {campo('motivo', op.tipo === 'AJUSTE' ? 'Motivo del ajuste (obligatorio para confirmar)' : 'Motivo', 2000, true)}
      {campo('observaciones', 'Observaciones', 2000, true)}
      <div className="fila"><button type="submit" className="principal" disabled={!cambiado || guardando}>Guardar cambios</button>
        {cambiado && <span className="suave pequena">Cambios sin guardar.</span>}</div>
    </form>
  );
}

function HistorialOperacion({ id }: { id: string }): ReactNode {
  const TEXTO: Readonly<Record<string, string>> = {
    CREADA: 'Creada', EDITADA: 'Cabecera editada', LINEA_ANADIDA: 'Línea añadida', LINEA_MODIFICADA: 'Línea modificada',
    LINEA_ELIMINADA: 'Línea eliminada', CONFIRMADA: 'Confirmada', ANULADA: 'Anulada',
  };
  const h = useHistorial<EventoOperacionStock>(`/api/inventario/operaciones/${id}/historial`);
  return (
    <div className="panel"><div className="panel-cuerpo">
      <Panel estado={h}>{(x) => (
        <ol className="lista-historial">
          {x.filas.map((e) => (
            <li key={e.id}><strong>{TEXTO[e.tipo] ?? e.tipo}</strong>
              {e.cambios !== null && <CambiosEvento cambios={e.cambios} />}
              <div className="suave pequena">{e.usuario.nombre} · {fechaHora(e.ocurridoEn)}</div></li>
          ))}
        </ol>
      )}</Panel>
      <PieHistorial historial={h} />
    </div></div>
  );
}

export function PaginaOperacion(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'lineas';
  const op = useDatos(() => api.get<OperacionStockDetalle>(`/api/inventario/operaciones/${encodeURIComponent(id)}`), [id]);
  const [dialogo, setDialogo] = useState<'confirmar' | 'anular' | 'rectificar' | null>(null);
  const [motivo, setMotivo] = useState('');
  const [editando, setEditando] = useState<LineaOperacionStock | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const o = op.datos;
  const migas = [{ texto: 'Inventario' }, { texto: 'Movimientos', a: '/inventario/movimientos' }, { texto: o?.codigo ?? '…' }];
  if (op.error instanceof FalloApi && (op.error.estado === 404 || op.error.estado === 403)) {
    return <Shell migas={migas} titulo="Operación no disponible"><div className="panel"><Vacio titulo="Esta operación no existe o no tienes acceso"><Link to="/inventario/movimientos">Volver</Link></Vacio></div></Shell>;
  }
  const ajusteOk = o === null || o.tipo !== 'AJUSTE' || puede('stock:ajustar');
  const borrador = o !== null && o.estado === 'BORRADOR';
  const edita = borrador && puede('operaciones_stock:gestionar') && ajusteOk;
  async function accion(f: () => Promise<unknown>): Promise<void> {
    setError(null);
    setOcupado(true);
    try {
      await f();
      setDialogo(null);
      setMotivo('');
      op.recargar();
    } catch (e) {
      setDialogo(null);
      setError(e);
    } finally {
      setOcupado(false);
    }
  }
  async function rectificar(): Promise<void> {
    if (o === null) return;
    setError(null);
    setOcupado(true);
    try {
      const nueva = await api.post<OperacionStockDetalle>(`/api/inventario/operaciones/${o.id}/rectificar`, { motivo });
      setDialogo(null);
      navegar(`/inventario/operaciones/${nueva.id}`);
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }
  const camposMotivo = erroresDe(error);
  return (
    <Shell dominio="movimientos" migas={migas} titulo={o === null ? 'Operación' : `${TIPOS_OPERACION[o.tipo]} ${o.codigo}`}
      subtitulo={o === null ? undefined : <span className="fila" style={{ flexWrap: 'wrap' }}><EstadoOperacion e={o.estado} />{o.origen !== null && <span className="suave">Rectifica <Link to={`/inventario/operaciones/${o.origen.id}`}>{o.origen.codigo}</Link></span>}</span>}
      acciones={o === null ? undefined : (
        <BarraAcciones etiqueta="Acciones de la operación">
          {borrador && puede('operaciones_stock:confirmar') && ajusteOk && <button className="principal" disabled={o.lineas.length === 0} onClick={() => setDialogo('confirmar')}>Confirmar</button>}
          {edita && <button onClick={() => setDialogo('anular')}>Anular borrador</button>}
          {o.estado === 'CONFIRMADA' && o.parte == null && o.ot == null && puede('operaciones_stock:crear') && (o.tipo !== 'AJUSTE' || puede('stock:ajustar')) && <button onClick={() => setDialogo('rectificar')}>Rectificar</button>}
        </BarraAcciones>
      )}
      tabs={<Pestanas opciones={[['lineas', 'Líneas'], ['historial', 'Historial']]} actual={tab} fijar={(k) => setParams({ tab: k })} />}>
      {error !== null && <ErrorStock error={error} />}
      <Panel estado={op}>
        {(d) => (
          <>
            {d.estado === 'BORRADOR' && <p className="nota aviso banner-borrador" role="status"><strong>Borrador.</strong> {TEXTO_BORRADOR}</p>}
            {d.estado === 'ANULADA' && <p className="nota" role="status">Anulada por {d.anuladoPor?.nombre} · {fechaHora(d.anuladoEn)}{d.motivoAnulacion !== null && ` — ${d.motivoAnulacion}`}. No movió stock.</p>}
            {tab === 'lineas' && borrador && d.tipo === 'AJUSTE' && (d.motivo ?? '').trim().length < 5 && (
              <p className="nota aviso" role="status">Falta el motivo del ajuste: añádelo en «Información de la operación» antes de confirmar.</p>
            )}
            {tab === 'lineas' && (
              <section className="panel" aria-label="Información de la operación">
                <div className="panel-cuerpo">
                  <h2 className="titulo-seccion">Información de la operación</h2>
                  <div className="datos">
                    <div className="dato"><div className="etiqueta-dato">Tipo</div><div className="valor-dato">{TIPOS_OPERACION[d.tipo]}</div></div>
                    {d.parte != null && (
                      <div className="dato"><div className="etiqueta-dato">Parte de trabajo</div>
                        <div className="valor-dato"><Link className="mono" to={`/mantenimiento/partes/${d.parte.id}#materiales`}>{d.parte.codigo}</Link></div></div>
                    )}
                    {d.ot != null && (
                      <div className="dato"><div className="etiqueta-dato">Orden de trabajo</div>
                        <div className="valor-dato"><Link className="mono" to={`/ots/${d.ot.id}#consumos`}>{d.ot.codigo}</Link></div></div>
                    )}
                    <div className="dato"><div className="etiqueta-dato">{d.tipo === 'TRASLADO' ? 'Origen' : 'Almacén'}</div><div className="valor-dato">{nombreAlmacen(d.almacen)}</div></div>
                    {d.almacenDestino !== null && <div className="dato"><div className="etiqueta-dato">Destino</div><div className="valor-dato">{nombreAlmacen(d.almacenDestino)}</div></div>}
                    {!edita && <div className="dato"><div className="etiqueta-dato">Documento</div><div className="valor-dato">{[d.tipoDocumento, d.numeroDocumento].filter(Boolean).join(' ') || '—'}{d.fechaDocumento != null && <span className="suave"> · {fecha(d.fechaDocumento)}</span>}</div></div>}
                    {!edita && d.motivo !== null && <div className="dato"><div className="etiqueta-dato">Motivo</div><div className="valor-dato">{d.motivo}</div></div>}
                    {!edita && d.observaciones !== null && <div className="dato"><div className="etiqueta-dato">Observaciones</div><div className="valor-dato">{d.observaciones}</div></div>}
                    <div className="dato"><div className="etiqueta-dato">Creada</div><div className="valor-dato">{d.creadoPor.nombre} · {fechaHora(d.creadoEn)}</div></div>
                    {d.confirmadoPor !== null && <div className="dato"><div className="etiqueta-dato">Confirmada</div><div className="valor-dato">{d.confirmadoPor.nombre} · {fechaHora(d.confirmadoEn)}</div></div>}
                  </div>
                  {edita && <CabeceraBorrador key={d.actualizadoEn} op={d} alGuardar={op.recargar} />}
                </div>
              </section>
            )}
            {tab === 'lineas' && (
              <section className="panel" aria-label="Líneas de la operación">
                <div className="panel-cuerpo">
                  <h2 className="titulo-seccion">Líneas</h2>
                  {d.lineas.length === 0 ? <p className="suave">Sin líneas todavía.</p> : (
                    <div className="tabla-desliza">
                      <table aria-label="Líneas">
                        <thead><tr><th>#</th><th>Artículo</th>{d.tipo === 'AJUSTE' && <th>Sentido</th>}<th>Cantidad</th>
                          {d.estado === 'BORRADOR' ? <th>{d.tipo === 'TRASLADO' ? 'Saldo origen / destino' : 'Saldo actual'}</th> : <th>En unidad de stock</th>}
                          {edita && <th />}</tr></thead>
                        <tbody>
                          {d.lineas.map((l) => (
                            <tr key={l.id}>
                              <td>{l.orden}</td>
                              <td>{nombreArticulo(l.articulo)}<TrazaLinea lote={l.lote} series={l.series} />
                                {d.ot != null && l.consumoOt != null && <div className="pequena"><Link to={`/ots/${d.ot.id}#consumo-${l.consumoOt.id}`}>Ver consumo en la OT</Link></div>}</td>
                              {d.tipo === 'AJUSTE' && <td>{l.sentido === 'ENTRADA' ? 'Suma' : 'Resta'}</td>}
                              <td><Cantidad v={l.cantidad} u={l.unidad.codigo} /></td>
                              {d.estado === 'BORRADOR'
                                ? <td className="suave">{l.saldoOrigen} {l.articulo.unidadStock.codigo}{l.saldoDestino !== null && ` / ${l.saldoDestino} ${l.articulo.unidadStock.codigo}`}</td>
                                : <td>{l.cantidadStock === null ? '—' : <Cantidad v={l.cantidadStock} u={l.articulo.unidadStock.codigo} />}{l.factorConversion !== null && l.factorConversion !== '1' && <span className="suave pequena"> (× {l.factorConversion})</span>}</td>}
                              {edita && (
                                <td><span className="fila">
                                  <button className="pequeno" onClick={() => setEditando(l)}>Editar</button>
                                  <button className="pequeno" onClick={() => void accion(() => api.del(`/api/inventario/operaciones/${d.id}/lineas/${l.id}`))}>Quitar</button>
                                </span></td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {d.estado === 'BORRADOR' && <p className="suave pequena">Los saldos son informativos: la disponibilidad se comprueba al confirmar.</p>}
                  {edita && <AnadirLinea op={d} alCambiar={op.recargar} />}
                  {d.parte != null && d.estado === 'CONFIRMADA' && (
                    <p className="pequena suave">Operación generada desde el parte {d.parte.codigo}. No se rectifica: si el material vuelve, se registra como devolución en el parte.</p>
                  )}
                  {d.ot != null && d.estado === 'CONFIRMADA' && (
                    <p className="pequena suave">Operación generada al validar consumos de la OT {d.ot.codigo}. No se rectifica: si el stock real difiere, corrígelo con un ajuste.</p>
                  )}
                  {d.inventario != null && (
                    <p className="pequena">Regularización del inventario <Link to={`/inventario/inventarios/${d.inventario.id}`}>{d.inventario.codigo}</Link>.</p>
                  )}
                  {d.rectificaciones.length > 0 && (
                    <p className="pequena">Rectificaciones: {d.rectificaciones.map((r, i) => <span key={r.id}>{i > 0 && ', '}<Link to={`/inventario/operaciones/${r.id}`}>{r.codigo}</Link> ({ESTADOS_OPERACION[r.estado][0].toLowerCase()})</span>)}</p>
                  )}
                </div>
              </section>
            )}
            {tab === 'historial' && <HistorialOperacion id={d.id} />}
            {editando !== null && <EditarLinea op={d} l={editando} cerrar={() => setEditando(null)} alCambiar={op.recargar} />}
            {dialogo === 'confirmar' && (
              <Modal titulo={`Confirmar ${d.codigo}`} cerrar={() => setDialogo(null)}
                pie={<><button onClick={() => setDialogo(null)}>Cancelar</button><button className="principal" disabled={ocupado} onClick={() => void accion(() => api.post(`/api/inventario/operaciones/${d.id}/confirmar`, { versionEsperada: d.actualizadoEn }))}>Confirmar operación</button></>}>
                <p>{TEXTO_CONFIRMAR}</p>
                <p className="suave pequena">{TIPOS_OPERACION[d.tipo]} · {d.lineas.length} línea(s) · {nombreAlmacen(d.almacen)}{d.almacenDestino !== null && ` → ${nombreAlmacen(d.almacenDestino)}`}</p>
              </Modal>
            )}
            {dialogo === 'anular' && (
              <Modal titulo={`Anular ${d.codigo}`} cerrar={() => setDialogo(null)}
                pie={<><button onClick={() => setDialogo(null)}>Cancelar</button><button className="peligro" disabled={ocupado} onClick={() => void accion(() => api.post(`/api/inventario/operaciones/${d.id}/anular`, { motivo, versionEsperada: d.actualizadoEn }))}>Anular borrador</button></>}>
                <p>El borrador quedará anulado y no podrá confirmarse. No ha movido stock.</p>
                <div className="campo"><label htmlFor="an-motivo">Motivo (opcional)</label><textarea id="an-motivo" rows={2} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} /></div>
              </Modal>
            )}
            {dialogo === 'rectificar' && (
              <Modal titulo={`Rectificar ${d.codigo}`} cerrar={() => setDialogo(null)}
                pie={<><button onClick={() => setDialogo(null)}>Cancelar</button><button className="principal" disabled={ocupado || motivo.trim().length < 5} onClick={() => void rectificar()}>Crear rectificación</button></>}>
                <p>La operación original no cambia. Se creará un <strong>borrador compensatorio</strong> con las mismas líneas en sentido contrario (mismos lotes y mismas series); ajusta las cantidades si la corrección es parcial y confírmalo.</p>
                <div className="campo"><label htmlFor="re-motivo">Motivo de la rectificación</label><textarea id="re-motivo" rows={2} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} />
                  {camposMotivo['motivo'] !== undefined && <div className="error-campo">{camposMotivo['motivo']}</div>}</div>
              </Modal>
            )}
          </>
        )}
      </Panel>
    </Shell>
  );
}
